/**
 * Copyright 2022 Phenix Real Time Solutions, Inc. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {getRendererStatisticsAndUpdate} from './statistics';

const CHANGE_VOLUME = 'CHANGE_VOLUME';
const CHANGE_MUTE_STATE = 'CHANGE_MUTE_STATE';
const CHANGE_VIDEO_ENABLED_STATE = 'CHANGE_VIDEO_ENABLED_STATE';
const SEEK_TO = 'SEEK_TO';
const SEEK_TO_REALTIME_STREAM = 'SEEK_TO_REALTIME_STREAM';
const UPDATE_BUFFER = 'UPDATE_BUFFER';
const CHANGE_PAUSE_STATE = 'CHANGE_PAUSE_STATE';
const CHANGE_FULLSCREEN_STATE = 'CHANGE_FULLSCREEN_STATE';
const changeVolumeAction = (video, volume) => {
  if (video) {
    volume = video.setVolume(volume);
  }

  return {
    type: CHANGE_VOLUME,
    volume
  };
};

const changeMuteStateAction = (video, muted) => {
  if (video) {
    if (video.getAudioMuted() !== muted) {
      video.setAudioMuted(muted);
    }

    video.rememberAudioState();

    muted = video.getAudioMuted();
  }

  return {
    type: CHANGE_MUTE_STATE,
    muted
  };
};

const updateMuteStateAction = (video) => {
  return {
    type: CHANGE_MUTE_STATE,
    muted: !video || video.getAudioMuted()
  };
};

const changeVideoEnabledStateAction = (video, videoState) => {
  if (video) {
    video.setVideoState(videoState);

    videoState = video.getVideoState();
  }

  return {
    type: CHANGE_VIDEO_ENABLED_STATE,
    videoState
  };
};

const seekToAction = (video, currentTime) => {
  if (video) {
    video.seekTo(currentTime);
  }

  return {
    type: SEEK_TO,
    currentTime
  };
};

const rewindByAction = (video, amountToRewind) => {
  let newCurrentTime = 0;

  if (video) {
    newCurrentTime = video.getCurrentTime() - amountToRewind;

    video.seekTo(newCurrentTime);
  }

  return {
    type: SEEK_TO,
    currentTime: newCurrentTime
  };
};

const updateBufferAction = (bufferStart, bufferEnd, currentTime) => {
  return {
    type: UPDATE_BUFFER,
    bufferStart,
    bufferEnd,
    currentTime
  };
};

const seekToRealtimeStreamAction = (video) => {
  if (video) {
    video.seekToRealTime();
  }

  return {type: SEEK_TO_REALTIME_STREAM};
};

const updatePauseStateAction = (video) => {
  return {
    type: CHANGE_PAUSE_STATE,
    paused: !video || !video.getVideoState()
  };
};

const changeFullscreenStateAction = (isFullscreen) => {
  return {
    type: CHANGE_FULLSCREEN_STATE,
    isFullscreen
  };
};

const changeFullscreen = (isFullscreen) => {
  return function(dispatch) {
    return dispatch(changeFullscreenStateAction(isFullscreen));
  };
};

const changeVolume = (video, volume) => {
  return function(dispatch) {
    if (volume === 0) {
      dispatch(changeMuteStateAction(video, true));
      dispatch(changeVolumeAction(video, 0.2));
    } else {
      dispatch(changeMuteStateAction(video, false));
      dispatch(changeVolumeAction(video, volume));
    }
  };
};

const changeMuteState = (video, muted) => {
  return function(dispatch) {
    dispatch(changeMuteStateAction(video, muted));
  };
};

const getAndSetBufferedDetails = (video, renderer) => {
  return function(dispatch) {
    if (video) {
      const bufferedStart = video.getBufferedStart();
      const bufferedEnd = video.getBufferedEnd();
      const currentTime = video.getCurrentTime();

      dispatch(updateBufferAction(bufferedStart, bufferedEnd, currentTime));
      dispatch(getRendererStatisticsAndUpdate(renderer));
    }
  };
};

export {
  seekToAction,
  rewindByAction,
  seekToRealtimeStreamAction,
  changeVolume,
  changeMuteState,
  changeVideoEnabledStateAction,
  updateBufferAction,
  updateMuteStateAction,
  updatePauseStateAction,
  changeFullscreen,
  getAndSetBufferedDetails,
  SEEK_TO,
  SEEK_TO_REALTIME_STREAM,
  CHANGE_VOLUME,
  CHANGE_MUTE_STATE,
  CHANGE_VIDEO_ENABLED_STATE,
  UPDATE_BUFFER,
  CHANGE_PAUSE_STATE,
  CHANGE_FULLSCREEN_STATE
};